/**
 * VirtDashboard — /virt/overview (resumen de la virtualización).
 *
 * Panel de un vistazo: cuánta infraestructura hay, cómo está repartida y qué
 * merece atención (VMs sin hipervisor, hosts sin CPU/RAM declaradas, agente
 * caído, datastores llenándose). Sigue el estándar de los otros resúmenes
 * (IPAM y WiFi):
 *  · misma cabecera que una lista (`.list-head`) y mismos chips de KPI, cada uno
 *    llevando a su pantalla;
 *  · iconos de `@heroicons/react` (los del menú de virtualización), ni un emoji;
 *  · colores solo con las variables del sistema de diseño.
 *
 * No hay fila de botones de acceso rápido: para eso está el menú lateral. Los
 * números sí son clicables y cada tarjeta tiene su «Ver todas →».
 *
 * OJO con los nombres de los campos: el host físico lleva `cpu_total_cores` y
 * `ram_total_gb` (no `cpu_cores` / `ram_gb`, que son de la VM). El dashboard
 * antiguo leía los de la VM en el hipervisor, así que la capacidad física salía
 * siempre a cero y el bloque de «físico vs virtual» no se pintaba nunca.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CpuChipIcon,
  Square3Stack3DIcon,
  ServerStackIcon,
  ComputerDesktopIcon,
  Squares2X2Icon,
  ArrowsRightLeftIcon,
  CircleStackIcon,
  SignalIcon,
  CloudIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const POWER_META = {
  on:        { label: 'Encendidas',  badge: 'list-badge--on' },
  off:       { label: 'Apagadas',    badge: 'list-badge--off' },
  suspended: { label: 'Suspendidas', badge: 'list-badge--warn' },
  unknown:   { label: 'Sin datos',   badge: 'list-badge' },
};

const AGENT_META = {
  online:     { label: 'Agente online',   badge: 'list-badge--on' },
  offline:    { label: 'Agente offline',  badge: 'list-badge--off' },
  'no-agent': { label: 'Sin agente',      badge: 'list-badge--warn' },
  unknown:    { label: 'Sin datos',       badge: 'list-badge' },
};

const DS_TYPE_LABEL = {
  vmfs:      'VMFS',
  nfs:       'NFS',
  iscsi:     'iSCSI',
  local:     'Local',
  ceph:      'Ceph',
  zfs:       'ZFS',
  smb:       'SMB',
  other:     'Otro',
};

/** Color de una barra según lo lleno que esté (o lo grave que sea). */
function tone(pct) {
  if (pct >= 90) return 'var(--red-600)';
  if (pct >= 70) return 'var(--amber-600)';
  return 'var(--green-600)';
}

/** Barra de progreso del estándar (la misma que en las columnas de las listas). */
function Bar({ pct, width = 90, color }) {
  const value = Math.max(0, Math.min(100, pct || 0));
  return (
    <span style={{
      display: 'inline-block', width, height: 7, borderRadius: 4,
      background: 'var(--bg-toolbar)', overflow: 'hidden', flexShrink: 0,
    }}>
      <span style={{ display: 'block', width: `${value}%`, height: '100%', background: color || tone(value) }} />
    </span>
  );
}

const cardStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md, 10px)',
};

const cardHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)',
};

export default function VirtDashboard({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();

  const [hypervisors, setHypervisors] = useState([]);
  const [clusters, setClusters]       = useState([]);
  const [vms, setVms]                 = useState([]);
  const [vswitches, setVswitches]     = useState([]);
  const [datastores, setDatastores]   = useState([]);
  const [nics, setNics]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [hyp, clu, vm, vsw, ds, nc] = await Promise.all([
        api.getHypervisors(companyId),
        api.getClusters(companyId),
        api.getVMs(companyId),
        api.getVSwitches(companyId),
        api.getDatastores(companyId).catch(() => ({ data: [] })),
        api.getVirtNics(companyId).catch(() => ({ data: [] })),
      ]);
      setHypervisors(hyp.data || []);
      setClusters(clu.data || []);
      setVms(vm.data || []);
      setVswitches(vsw.data || []);
      setDatastores(ds.data || []);
      setNics(nc.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando el resumen de virtualización');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ── Cuentas ────────────────────────────────────────────────────────────── */
  const k = useMemo(() => {
    const byStatus = (list, field) => list.reduce((acc, item) => {
      const key = item[field] || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const byPower = byStatus(vms, 'power_state');
    const byAgent = byStatus(vms, 'agent_status');

    const onVms = vms.filter(v => v.power_state === 'on').length;
    const templates = vms.filter(v => v.is_template).length;
    // Las delegadas no tienen hipervisor visible (`hosting_hidden`) y las que
    // declaran un proveedor externo (Azure, AWS, VPS…) tampoco están en
    // nuestros hosts: ninguna de las dos es una «VM sin hipervisor». Solo es
    // incidencia un hueco de verdad.
    const noHypervisor = vms.filter(
      v => !v.hypervisor_id && !v.hosting_hidden && !v.hosting_is_external
    );
    // Cloud / VPS: lo que no corre en un hipervisor propio visible.
    const cloudVms = vms.filter(v => v.hosting_hidden || v.hosting_is_external);
    const delegatedOut = vms.filter(v => String(v.company_id) === String(companyId) && v.delegated_company_id);
    const delegatedIn = vms.filter(v => String(v.company_id) !== String(companyId)
      && String(v.delegated_company_id) === String(companyId));

    /* Sistemas operativos: los 5 que más VMs agrupan. */
    const osTop = Object.entries(vms.reduce((acc, v) => {
      const os = v.os_name || tr('virt.no_os', 'Sin sistema operativo');
      acc[os] = (acc[os] || 0) + 1;
      return acc;
    }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5);

    /* Capacidad: lo declarado por las VMs contra lo declarado por los hosts. */
    const vmCpu = vms.reduce((s, v) => s + (v.cpu_cores || 0), 0);
    const vmRam = vms.reduce((s, v) => s + (v.ram_gb || 0), 0);
    const vmDisk = vms.reduce((s, v) => s + (v.disk_total_gb || 0), 0);
    const hypCpu = hypervisors.reduce((s, h) => s + (h.cpu_total_cores || 0), 0);
    const hypRam = hypervisors.reduce((s, h) => s + (h.ram_total_gb || 0), 0);
    const hypDisk = hypervisors.reduce((s, h) => s + (h.storage_local_tb || 0), 0) * 1024;
    const hostsWithoutSpecs = hypervisors.filter(h => !h.cpu_total_cores && !h.ram_total_gb).length;
    const hostsWithoutServer = hypervisors.filter(h => !h.rack_element_id).length;
    const hostsAgentOffline = hypervisors.filter(h => (h.agent_status || 'unknown') === 'offline').length;

    /* Redes virtuales. */
    const allPgs = vswitches.flatMap(v => v.port_groups || []);
    const distributed = vswitches.filter(v => v.vswitch_type === 'distributed').length;
    const jumbo = vswitches.filter(v => (v.mtu || 0) >= 9000).length;
    const noUplink = vswitches.filter(v => !(v.uplink_count > 0)).length;
    const managementPgs = allPgs.filter(pg => pg.pg_type === 'management').length;
    const storagePgs = allPgs.filter(pg => pg.pg_type === 'storage').length;
    const vlansInUse = [...new Set(allPgs.map(pg => pg.vlan_id).filter(n => n))]
      .sort((a, b) => a - b);

    /* Hosts ordenados por VMs (el que más carga, primero). */
    const hostRows = hypervisors.map(h => ({
      ...h,
      _vms: vms.filter(v => String(v.hypervisor_id) === String(h.id)).length,
    })).sort((a, b) => b._vms - a._vms);

    /* Clusters con su ocupación de nodos. */
    const clusterRows = clusters.map(c => ({
      ...c,
      _vms: vms.filter(v => String(v.cluster_id) === String(c.id)).length,
      _incomplete: Boolean(c.total_nodes) && (c.node_count || 0) < c.total_nodes,
    })).sort((a, b) => b._vms - a._vms);

    /* Datastores: uso agregado y los que están llenos. */
    const dsTotal = datastores.reduce((s, d) => s + (d.total_gb || 0), 0);
    const dsUsed = datastores.reduce((s, d) => s + (d.used_gb || 0), 0);
    const dsFull = datastores.filter(d => (d.used_pct ?? 0) >= 85).length;

    const vmsWithNetwork = new Set(nics.map(n => n.vm_id)).size;

    return {
      byPower, byAgent, onVms, templates, noHypervisor, delegatedOut, delegatedIn, cloudVms,
      osTop, vmCpu, vmRam, vmDisk, hypCpu, hypRam, hypDisk, hostsWithoutSpecs,
      hostsWithoutServer, hostsAgentOffline, allPgs, distributed, jumbo, noUplink,
      managementPgs, storagePgs, vlansInUse, hostRows, clusterRows,
      dsTotal, dsUsed, dsFull, vmsWithNetwork,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vms, hypervisors, clusters, vswitches, datastores, nics, companyId, tr]);

  const kpiChips = [
    { key: 'hyp', icon: ServerStackIcon, label: tr('virt.hyp_title', 'Hipervisores'), value: hypervisors.length, path: '/virt/hypervisors' },
    { key: 'clu', icon: Squares2X2Icon, label: tr('virt.cluster_title', 'Clusters'), value: clusters.length, path: '/virt/clusters' },
    { key: 'vms', icon: ComputerDesktopIcon, label: tr('virt.vm_title', 'Máquinas virtuales'), value: vms.length, path: '/virt/vms' },
    { key: 'on', icon: SignalIcon, label: tr('virt.vm_on', 'Encendidas'), value: k.onVms, path: '/virt/vms?power=on' },
    { key: 'tpl', icon: CircleStackIcon, label: tr('virt.template', 'Plantillas'), value: k.templates, path: '/virt/vms?template=yes' },
    { key: 'cloud', icon: CloudIcon, label: tr('virt.cloud_title', 'Cloud / VPS'), value: k.cloudVms, path: '/virt/cloud' },
    { key: 'vsw', icon: ArrowsRightLeftIcon, label: 'vSwitches', value: vswitches.length, path: '/virt/vswitches' },
  ];

  const subtitle = `${hypervisors.length} ${tr('virt.hyp_plural', 'hipervisores')}`
    + ` · ${vms.length} ${tr('virt.vm_plural', 'máquinas virtuales')}`
    + ` · ${vswitches.length} ${tr('virt.vsw_plural', 'vSwitches')}`;

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('virt.select_company', 'Selecciona una empresa para ver la virtualización.')}
        </div>
      </div>
    );
  }

  const dsPct = k.dsTotal > 0 ? Math.round((k.dsUsed / k.dsTotal) * 100) : 0;
  const maxOs = Math.max(1, ...k.osTop.map(([, count]) => count));

  /**
   * Lista de hosts. A propósito NO es una `<table>` de Bootstrap: dentro de una
   * tarjeta del panel las tablas se pintaban con fondo oscuro (el CMDB arrastra
   * reglas de tabla del tema base) y rompían la estética del resto del panel.
   * Aquí son filas con los mismos tokens que las tarjetas de clusters y redes.
   */
  const hostRows2 = (
    <div>
      {k.hostRows.map(h => (
        <div
          key={h.id}
          role="button"
          tabIndex={0}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '6px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer',
          }}
          onClick={() => navigate(`/virt/hypervisors?destacar=${h.id}&abrir=1`)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/virt/hypervisors?destacar=${h.id}&abrir=1`); }}
          title={`${tr('action.view', 'Ver')} ${h.name}`}
        >
          <span style={{ display: 'inline-flex', flexDirection: 'column', minWidth: 150 }}>
            <span style={{ fontWeight: 600 }}>{h.name}</span>
            <span className="list-hint">{h.hypervisor_type_label || h.hypervisor_type}</span>
          </span>
          <span className={`list-badge ${h.cluster_name ? '' : 'list-badge--off'}`}>
            {h.cluster_name || tr('virt.no_cluster', 'Suelto')}
          </span>
          <span className="list-badge">{h._vms} VMs</span>
          <span className="list-code">
            {h.cpu_total_cores || h.ram_total_gb
              ? `${h.cpu_total_cores || '—'}C / ${h.ram_total_gb || '—'}GB`
              : '—'}
          </span>
          <span
            className={`list-badge ${h.status === 'active' ? 'list-badge--on' : h.status === 'decom' ? 'list-badge--off' : 'list-badge--warn'}`}
            style={{ marginLeft: 'auto' }}
          >
            {h.status || 'active'}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="list-page" style={{ padding: '1.5rem' }}>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* ══ Cabecera, igual que la de una lista ══ */}
      <div className="list-head">
        <div className="list-head-left">
          <CpuChipIcon className="list-head-icon" />
          <div>
            <h1 className="list-title">{tr('virt.dash_title', 'Virtualización')}</h1>
            <p className="list-subtitle">
              {loading ? tr('msg.loading', 'Cargando…') : subtitle}
            </p>
          </div>
        </div>
        <div className="list-head-actions">
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => navigate('/virt/topology')}
            title={tr('virt.view_topology', 'Ver la topología de virtualización')}
          >
            <Square3Stack3DIcon style={{ width: 15, height: 15 }} /> {tr('virt.topology', 'Topología')}
          </button>
        </div>
      </div>

      {/* ══ Chips de KPI: cada uno lleva a su pantalla ══ */}
      <div className="list-kpis list-kpis--chips" style={{ marginBottom: 16 }}>
        {kpiChips.map(chip => {
          const Icon = chip.icon;
          return (
            <button
              key={chip.key}
              type="button"
              className="list-kpi-chip"
              onClick={() => navigate(chip.path)}
              title={`${tr('dash.go_to', 'Ir a')} ${chip.label}`}
            >
              <span className="list-kpi-value">{chip.value}</span>
              <span className="list-kpi-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Icon style={{ width: 13, height: 13 }} />
                {chip.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* ══ Avisos: lo que hay que mirar ══ */}
      {(k.noHypervisor.length > 0 || k.hostsWithoutSpecs > 0 || k.dsFull > 0) && (
        <div
          style={{
            display: 'flex', flexDirection: 'column', gap: 6,
            border: '1px solid var(--border)', borderLeft: '3px solid var(--amber-600)',
            background: 'var(--bg-card)', borderRadius: 'var(--radius-md, 10px)',
            padding: '10px 14px', marginBottom: 16,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <ExclamationTriangleIcon style={{ width: 18, height: 18, color: 'var(--amber-600)', flexShrink: 0 }} />
            <strong>{tr('virt.attention', 'Merece atención')}</strong>
          </span>
          {k.noHypervisor.length > 0 && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none text-start"
              onClick={() => navigate('/virt/vms?hypervisorId=none')}
              title={tr('virt.see_them', 'Verlas')}
            >
              {k.noHypervisor.length} {tr('virt.no_hypervisor_plural', 'VMs sin hipervisor asignado')}
            </button>
          )}
          {k.hostsWithoutSpecs > 0 && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none text-start"
              onClick={() => navigate('/virt/hypervisors')}
              title={tr('virt.see_them', 'Verlos')}
            >
              {k.hostsWithoutSpecs} {tr('virt.hosts_without_specs', 'hipervisores sin CPU/RAM declaradas')}
            </button>
          )}
          {k.dsFull > 0 && (
            <span>
              {k.dsFull} {tr('virt.ds_full_plural', 'datastores por encima del 85 % de ocupación')}
            </span>
          )}
        </div>
      )}

      <div className="row g-3">
        {/* ══ Estado de las VMs ══ */}
        <div className="col-lg-4">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ComputerDesktopIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('virt.vm_status', 'Estado de las VMs')}
              </strong>
              <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/virt/vms')}>
                {tr('dash.see_all', 'Ver todas')} →
              </button>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {vms.length === 0 && <span className="list-hint">{tr('virt.vm_empty', 'No hay máquinas virtuales')}</span>}
              {vms.length > 0 && Object.entries(POWER_META).map(([state, meta]) => {
                const count = k.byPower[state] || 0;
                if (!count) return null;
                const pct = Math.round((count / vms.length) * 100);
                return (
                  <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <span className={`list-badge ${meta.badge}`} style={{ minWidth: 100 }}>{meta.label}</span>
                    <Bar pct={pct} width={70} />
                    <span className="list-hint" style={{ marginLeft: 'auto' }}>{count} · {pct}%</span>
                  </div>
                );
              })}
              {k.templates > 0 && (
                <div className="list-hint" style={{ marginTop: 8 }}>
                  {k.templates} {tr('virt.template_plural', 'marcadas como plantilla')}
                </div>
              )}
              {k.delegatedOut.length > 0 && (
                <div className="list-hint">
                  {k.delegatedOut.length} {tr('virt.delegated_out_plural', 'cedidas a otra empresa')}
                  {k.delegatedIn.length > 0 && ` · ${k.delegatedIn.length} ${tr('virt.delegated_in_plural', 'recibidas por delegación')}`}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Agente CMDB ══ */}
        <div className="col-lg-4">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SignalIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('virt.agent', 'Agente')}
              </strong>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {vms.length === 0 && <span className="list-hint">{tr('virt.vm_empty', 'No hay máquinas virtuales')}</span>}
              {vms.length > 0 && Object.entries(AGENT_META).map(([state, meta]) => {
                const count = k.byAgent[state] || 0;
                if (!count) return null;
                const pct = Math.round((count / vms.length) * 100);
                return (
                  <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <span className={`list-badge ${meta.badge}`} style={{ minWidth: 110 }}>{meta.label}</span>
                    <Bar pct={pct} width={60} color={state === 'offline' ? 'var(--red-600)' : undefined} />
                    <span className="list-hint" style={{ marginLeft: 'auto' }}>{count}</span>
                  </div>
                );
              })}
              <div className="list-hint" style={{ marginTop: 8 }}>
                {k.vmsWithNetwork} {tr('virt.vms_with_network', 'VMs con alguna NIC declarada')}
              </div>
              {k.hostsAgentOffline > 0 && (
                <div className="list-hint" style={{ color: 'var(--red-600)' }}>
                  {k.hostsAgentOffline} {tr('virt.hosts_agent_offline', 'hipervisores con el agente caído')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Sistemas operativos ══ */}
        <div className="col-lg-4">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Squares2X2Icon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('virt.os', 'Sistemas operativos')}
              </strong>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {k.osTop.length === 0 && <span className="list-hint">{tr('virt.no_os_data', 'Ninguna VM tiene sistema operativo declarado.')}</span>}
              {k.osTop.map(([os, count]) => (
                <div key={os} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span style={{ minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={os}>{os}</span>
                  <Bar pct={(count / maxOs) * 100} width={70} />
                  <span className="list-hint" style={{ marginLeft: 'auto' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ Capacidad: físico vs virtual ══ */}
        <div className="col-lg-6">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <CpuChipIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('virt.capacity_ratio', 'Capacidad: físico vs virtual')}
              </strong>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {k.hypCpu === 0 && k.hypRam === 0 ? (
                <span className="list-hint">
                  {tr('virt.no_specs_hint', 'Declara CPU y RAM en los hipervisores para ver el ratio de sobreasignación.')}
                </span>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <span style={{ minWidth: 120 }}>{tr('virt.vcpu_vs_cores', 'vCPU vs cores')}</span>
                    <Bar pct={k.hypCpu ? (k.vmCpu / k.hypCpu) * 100 : 0} width={140} />
                    <span className="list-code" style={{ marginLeft: 'auto' }}>
                      {k.vmCpu} / {k.hypCpu || '—'}
                      {k.hypCpu > 0 && ` · ${Math.round((k.vmCpu / k.hypCpu) * 100)}%`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <span style={{ minWidth: 120 }}>{tr('virt.vram_vs_ram', 'vRAM vs RAM')}</span>
                    <Bar pct={k.hypRam ? (k.vmRam / k.hypRam) * 100 : 0} width={140} />
                    <span className="list-code" style={{ marginLeft: 'auto' }}>
                      {k.vmRam} / {k.hypRam || '—'} GB
                      {k.hypRam > 0 && ` · ${Math.round((k.vmRam / k.hypRam) * 100)}%`}
                    </span>
                  </div>
                </>
              )}

              <div className="list-hint" style={{ marginTop: 10 }}>
                {tr('virt.vm_disk_total', 'Disco declarado en las VMs:')} <strong>{k.vmDisk} GB</strong>
                {k.hypDisk > 0 && <> · {tr('virt.local_disk_total', 'almacenamiento local de los hosts:')} <strong>{k.hypDisk} GB</strong></>}
              </div>
              {k.hostsWithoutSpecs > 0 && (
                <div className="list-hint">
                  {k.hostsWithoutSpecs} {tr('virt.hosts_without_specs', 'hipervisores sin CPU/RAM declaradas')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Hipervisores ══ */}
        <div className="col-lg-6">
          <div style={{ ...cardStyle, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ServerStackIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('virt.hyp_title', 'Hipervisores')}
              </strong>
              <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/virt/hypervisors')}>
                {tr('dash.see_all', 'Ver todos')} →
              </button>
            </div>
            <div style={{ padding: '12px 16px 12px' }}>
              {hypervisors.length === 0
                ? <span className="list-hint">{tr('virt.hyp_empty', 'No hay hipervisores')}</span>
                : hostRows2}
            </div>
            {k.hostsWithoutServer > 0 && (
              <div className="list-hint" style={{ padding: '0 16px 12px' }}>
                {k.hostsWithoutServer} {tr('virt.hosts_without_server', 'sin servidor físico asignado')}
              </div>
            )}
          </div>
        </div>

        {/* ══ Clusters ══ */}
        <div className="col-lg-6">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Squares2X2Icon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('virt.cluster_title', 'Clusters')}
              </strong>
              <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/virt/clusters')}>
                {tr('dash.see_all', 'Ver todos')} →
              </button>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {clusters.length === 0 && (
                <span className="list-hint">{tr('virt.cluster_empty', 'No hay clusters')}</span>
              )}
              {k.clusterRows.map(c => {
                const nodes = c.node_count || 0;
                const total = c.total_nodes || 0;
                return (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/virt/clusters?destacar=${c.id}&abrir=1`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/virt/clusters?destacar=${c.id}&abrir=1`); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      padding: '6px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                    }}
                    title={`${tr('action.view', 'Ver')} ${c.name}`}
                  >
                    <span style={{ fontWeight: 600, minWidth: 130 }}>{c.name}</span>
                    <span className={`list-badge ${c._incomplete ? 'list-badge--warn' : ''}`.trim()}>
                      {nodes}{total ? ` / ${total}` : ''} {tr('virt.nodes', 'nodos')}
                    </span>
                    {c.ha_enabled && <span className="list-badge list-badge--on">HA</span>}
                    {c.drs_enabled && <span className="list-badge">DRS</span>}
                    {c.is_hyperconverged && <span className="list-badge">HCI</span>}
                    <span className="list-hint" style={{ marginLeft: 'auto' }}>{c._vms} VMs</span>
                  </div>
                );
              })}
              {clusters.length > 0 && (
                <div className="list-hint" style={{ marginTop: 8 }}>
                  {clusters.filter(c => c.ha_enabled).length} {tr('virt.ha_yes_plural', 'con HA')}
                  {' · '}{clusters.filter(c => c.drs_enabled).length} {tr('virt.drs_yes_plural', 'con DRS')}
                  {' · '}{clusters.filter(c => c.is_hyperconverged).length} HCI
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Redes virtuales ══ */}
        <div className="col-lg-6">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ArrowsRightLeftIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('virt.vsw_title', 'Redes virtuales')}
              </strong>
              <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/virt/vswitches')}>
                {tr('dash.see_all', 'Ver todas')} →
              </button>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {vswitches.length === 0 ? (
                <span className="list-hint">{tr('virt.vsw_empty', 'No hay vSwitches')}</span>
              ) : (
                <>
                  <div className="list-hint">
                    {vswitches.length} vSwitches · {k.allPgs.length} {tr('virt.port_groups', 'port groups')}
                    {k.distributed > 0 && ` · ${k.distributed} ${tr('virt.distributed_plural', 'distribuidos')}`}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
                    {Object.entries(k.allPgs.reduce((acc, pg) => {
                      const key = pg.pg_type || 'vm';
                      acc[key] = (acc[key] || 0) + 1;
                      return acc;
                    }, {})).map(([type, count]) => (
                      <span key={type} className="list-badge">{type} · {count}</span>
                    ))}
                  </div>
                  {(k.managementPgs > 0 || k.storagePgs > 0) && (
                    <div className="list-hint">
                      {k.managementPgs > 0 && `${k.managementPgs} ${tr('virt.pg_management', 'de gestión')}`}
                      {k.managementPgs > 0 && k.storagePgs > 0 && ' · '}
                      {k.storagePgs > 0 && `${k.storagePgs} ${tr('virt.pg_storage', 'de almacenamiento')}`}
                    </div>
                  )}
                  {k.jumbo > 0 && (
                    <div className="list-hint">
                      {k.jumbo} {tr('virt.jumbo_plural', 'con MTU jumbo (9000)')}
                    </div>
                  )}
                  {k.noUplink > 0 && (
                    <div className="list-hint" style={{ color: 'var(--amber-600)' }}>
                      {k.noUplink} {tr('virt.no_uplink_plural', 'sin uplinks (aislados)')}
                    </div>
                  )}
                  {k.vlansInUse.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div className="list-hint">{tr('virt.vlans_in_use', 'VLANs en uso')}</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {k.vlansInUse.slice(0, 12).map(vlan => (
                          <span key={vlan} className="list-code">{vlan}</span>
                        ))}
                        {k.vlansInUse.length > 12 && (
                          <span className="list-hint">+{k.vlansInUse.length - 12}</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ══ Datastores (solo si hay) ══ */}
        {datastores.length > 0 && (
          <div className="col-12">
            <div style={cardStyle}>
              <div style={cardHeader}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <CircleStackIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  {tr('virt.ds_title', 'Datastores')}
                </strong>
                <span className="list-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {k.dsUsed} / {k.dsTotal} GB
                  <Bar pct={dsPct} width={90} />
                  <strong>{dsPct}%</strong>
                </span>
              </div>
              <div style={{ padding: '12px 16px' }}>
                {datastores.map(d => (
                  <div
                    key={d.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      padding: '6px 0', borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <span style={{ display: 'inline-flex', flexDirection: 'column', minWidth: 170 }}>
                      <span style={{ fontWeight: 600 }}>{d.name}</span>
                      <span className="list-hint">{d.cluster_name || d.hypervisor_name || '—'}</span>
                    </span>
                    <span className="list-badge">{DS_TYPE_LABEL[d.datastore_type] || d.datastore_type || '—'}</span>
                    <span className="list-code">{d.total_gb ? `${d.total_gb} GB` : '—'}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Bar pct={d.used_pct ?? 0} width={120} />
                      <span className="list-hint">
                        {d.used_gb ?? 0} GB{d.used_pct != null ? ` · ${d.used_pct}%` : ''}
                      </span>
                    </span>
                    <span
                      className={`list-badge ${d.status === 'active' ? 'list-badge--on' : ''}`.trim()}
                      style={{ marginLeft: 'auto' }}
                    >
                      {d.status || 'active'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
